import { Provider, EnvironmentProviders } from '@angular/core';
import { provideIcons } from '@ng-icons/core';
import { provideMonacoEditor } from 'ngx-monaco-editor-v2';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { LUCIDE_ICONS, LucideIconProvider } from 'lucide-angular';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Bot,
  Box,
  Briefcase,
  Building2,
  Calendar,
  CalendarPlus,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  CircleDashed,
  CircleDot,
  CircleMinus,
  CirclePlus,
  CircleQuestionMark,
  Clipboard,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Code,
  Copy,
  Cpu,
  Database,
  Download,
  Ellipsis,
  EllipsisVertical,
  ExternalLink,
  Eye,
  File,
  FileSpreadsheet,
  FileText,
  Filter,
  Flag,
  Folder,
  FolderOpen,
  FolderPlus,
  GitBranch,
  GitMerge,
  GitPullRequest,
  Github,
  Globe,
  Handshake,
  Hash,
  History,
  Home,
  Image,
  Info,
  KeyRound,
  Languages,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  Library,
  Link,
  List,
  Lock,
  LogOut,
  Mail,
  MailCheck,
  MapPin,
  Maximize,
  MessageSquare,
  MessagesSquare,
  Mic,
  Minimize,
  Minus,
  Monitor,
  Moon,
  Package,
  PanelLeft,
  Pencil,
  Phone,
  Pin,
  Plus,
  PlusCircle,
  RefreshCw,
  Rocket,
  RotateCcw,
  Palette,
  SunMoon,
  Type,
  CalendarClock,
  PenLine,
  Save,
  ScrollText,
  Search,
  Send,
  Server,
  Settings,
  Share2,
  Shield,
  ShieldPlus,
  ShoppingBag,
  SlidersHorizontal,
  Sparkles,
  SquarePen,
  Star,
  Store,
  Sun,
  Tag,
  ThumbsUp,
  Trash2,
  Truck,
  Upload,
  User,
  UserPlus,
  Users,
  Video,
  Volume2,
  Wrench,
  X,
  Zap,
} from 'lucide-angular';
import { MyPresetTwo } from 'my-preset-two';
import { ThemeService } from '@app/shared/theme/theme.service';
import { MIGRATED_LUCIDE_ICONS } from '@app/shared/svg-icon/sprite-lucide-map';

/**
 * Providers for the custom primecontrol-* kit (ported from brandbot.webapp):
 * PrimeNG (styled via @primeuix preset), Lucide icon registry, theme service.
 *
 * Spread into the standalone app config: `providers: [...KIT_PROVIDERS]`
 * (see src/app/app.config.ts).
 */
export const KIT_PROVIDERS: (Provider | EnvironmentProviders)[] = [
  DialogService,
  MessageService,
  ThemeService,
  provideIcons({}),
  // ngx-monaco-editor-v2 loads `${baseUrl}/loader.js` and maps the require `vs`
  // path to baseUrl. angular.json copies monaco-editor/min/vs (flattened) into
  // assets/monaco-editor, so loader.js lives at assets/monaco-editor/loader.js.
  // Matches the proven brandbot setup. (A wrong path 404s and the SPA serves
  // index.html — the leading '<' that breaks loader.js parsing.)
  provideMonacoEditor({ baseUrl: 'assets/monaco-editor' }),
  providePrimeNG({
    ripple: true,
    theme: {
      preset: MyPresetTwo,
      options: {
        darkModeSelector: '.dark',
        // No CSS cascade layers: the app is still on Tailwind 3 (Pc),
        // which doesn't emit layered styles.
        cssLayer: false,
      },
    },
  }),
  {
    provide: LUCIDE_ICONS,
    multi: true,
    useValue: new LucideIconProvider({
      // Icons translated from the legacy SVG-sprite system (see sprite-lucide-map).
      ...MIGRATED_LUCIDE_ICONS,
      AlertTriangle,
      ArrowLeft,
      ArrowLeftRight,
      ArrowRight,
      ArrowUpRight,
      BookOpen,
      Bot,
      Box,
      Briefcase,
      Building2,
      Calendar,
      CalendarPlus,
      Check,
      ChevronDown,
      ChevronLeft,
      ChevronRight,
      CircleCheck,
      CircleDashed,
      CircleDot,
      CircleMinus,
      CirclePlus,
      CircleQuestionMark,
      Clipboard,
      ClipboardCheck,
      ClipboardList,
      Clock,
      Code,
      Copy,
      Cpu,
      Database,
      Download,
      Ellipsis,
      EllipsisVertical,
      ExternalLink,
      Eye,
      File,
      FileSpreadsheet,
      FileText,
      Filter,
      Flag,
      Folder,
      FolderOpen,
      FolderPlus,
      GitBranch,
      GitMerge,
      GitPullRequest,
      Github,
      Globe,
      Handshake,
      Hash,
      History,
      Home,
      Image,
      Info,
      KeyRound,
      Languages,
      Layers,
      LayoutDashboard,
      LayoutGrid,
      Library,
      Link,
      List,
      Lock,
      LogOut,
      Mail,
      MailCheck,
      MapPin,
      Maximize,
      MessageSquare,
      MessagesSquare,
      Mic,
      Minimize,
      Minus,
      Monitor,
      Moon,
      Package,
      PanelLeft,
      Pencil,
      Phone,
      Pin,
      Plus,
      PlusCircle,
      RefreshCw,
      Rocket,
      RotateCcw,
      Palette,
      SunMoon,
      Type,
      CalendarClock,
      PenLine,
      Save,
      ScrollText,
      Search,
      Send,
      Server,
      Settings,
      Share2,
      Shield,
      ShieldPlus,
      ShoppingBag,
      SlidersHorizontal,
      Sparkles,
      SquarePen,
      Star,
      Store,
      Sun,
      Tag,
      ThumbsUp,
      Trash2,
      Truck,
      Upload,
      User,
      UserPlus,
      Users,
      Video,
      Volume2,
      Wrench,
      X,
      Zap,
    }),
  },
];
